import { LoginOutput, RefreshInput, RefreshOutput } from '@allocab/allocab-commons-shared-types';
import { differenceInMinutes } from 'date-fns';

import { updateTokens } from './auth';
import { fetchTokens } from './useTokens';
import { queryClient, queryKeys } from '../datastore';
import type { ApiClient } from '../network/types';

/** Marge avant expiration en dessous de laquelle on rafraîchit. */
const REFRESH_THRESHOLD_IN_MINUTES = 5;

/**
 * Fabrique le `freshTokensCallback` d'un client `Api`.
 *
 * Le rafraîchissement de token était écrit une fois par client — `driverApi`,
 * `boApi`, `passengerApi`, plus celui de `driver-app` — pour une seule et même
 * logique. Ce qui les différenciait n'était que **l'instance qui poste le
 * rafraîchissement**, d'où ce paramètre.
 *
 * `getApi` est une fonction et non une instance : elle est appelée au moment du
 * rafraîchissement, ce qui permet à un client de se passer lui-même en argument
 * sans dépendance circulaire à la construction.
 *
 * ⚠️ Le rafraîchissement doit partir de l'instance de l'app hôte, pas d'une
 * autre : c'est elle qui porte ses en-têtes et son traitement d'`INVALID_TOKEN`.
 */
/**
 * Rafraîchit `tokens` et renvoie le nouvel access token. Extrait pour pouvoir
 * être partagé entre appelants concurrents, cf. `createFreshTokensGetter`.
 */
const refreshTokens = async (getApi: () => ApiClient, tokens: LoginOutput): Promise<string> => {
  const data = await getApi().post<RefreshInput, RefreshOutput>(
    '/authentication/v1/token/refresh',
    { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken },
    { skipAuth: true },
  );

  const refreshedTokens = {
    accessToken: data.accessToken,
    refreshToken: tokens.refreshToken,
    issued: data.issued,
    expires: data.expires,
  };

  // La session a pu changer pendant le POST : déconnexion (`clearTokens` a mis le
  // cache à `null`), ou un autre rafraîchissement concurrent déjà écrit. Écrire
  // quand même ressusciterait une session fermée — que `useTokens` repersisterait
  // aussitôt — ou écraserait des tokens plus récents. On ne persiste que si le
  // cache porte encore les tokens qu'on a rafraîchis ; la requête en cours, elle,
  // repart avec le token frais dans les deux cas.
  const currentTokens = queryClient.getQueryData<LoginOutput | null>(queryKeys.tokens());
  if (currentTokens?.accessToken === tokens.accessToken) {
    updateTokens(queryClient, refreshedTokens);
  }

  return refreshedTokens.accessToken;
};

export const createFreshTokensGetter = (getApi: () => ApiClient) => {
  /**
   * Le rafraîchissement en cours, partagé par tous les appelants qui arrivent
   * pendant qu'il tourne.
   *
   * Sans lui, **chaque requête en vol lançait son propre `POST /token/refresh`** :
   * le getter est appelé par l'intercepteur de requête, et un écran qui monte
   * quatre `useQuery` avec un token à moins de 5 min de l'expiration partait en
   * quatre rafraîchissements. Mesuré en production sur `passenger-app` : quatre
   * `POST /authentication/v1/token/refresh` en 130 ms à l'ouverture de l'accueil
   * (breadcrumbs de PASSENGER-3SP), et **neuf en sept secondes** quand le back
   * répondait du HTML — chacun capturé à part dans PASSENGER-4MW, pour une seule
   * panne vue par un seul utilisateur.
   *
   * Un seul POST part désormais ; les autres attendent le même résultat, succès
   * comme échec. Le partage se fait au niveau du getter, donc **par client**
   * (`passengerApi`, `driverApi`, `boApi` ont chacun le leur) — c'est le bon
   * grain, chaque client rafraîchit sur sa propre instance. `finally` libère la
   * place quel que soit le dénouement, pour que l'appel suivant reparte à neuf.
   */
  let inFlightRefresh: Promise<string> | null = null;

  return async (): Promise<string | null> => {
    // `ensureQueryData` et non `getQueryData` : celui-ci renvoie `undefined` tant
    // que `useTokens` n'a pas tourné, et la requête partait alors **sans bearer**.
    // Visible surtout sur `driver-app`, qui émet des requêtes avant tout rendu —
    // réveil par push, socket, suivi de position.
    //
    // ⚠️ Les trois options qui suivent ne sont pas décoratives, elles reprennent
    // celles de `useTokens`. `networkMode` surtout : au défaut `'online'`, quand
    // le cache est froid et l'appareil hors réseau, `ensureQueryData` retombe sur
    // `fetchQuery`, que react-query **met en pause** — la promesse ne résout
    // jamais et toute requête authentifiée reste bloquée. Lire le stockage ne
    // dépend pas du réseau : `'always'` est le seul mode correct ici.
    const tokens = await queryClient.ensureQueryData<LoginOutput | null>({
      queryKey: queryKeys.tokens(),
      queryFn: fetchTokens,
      networkMode: 'always',
      staleTime: Infinity,
      gcTime: Infinity,
    });

    if (!tokens) {
      return null;
    }

    if (differenceInMinutes(new Date(tokens.expires), Date.now()) < REFRESH_THRESHOLD_IN_MINUTES) {
      if (!inFlightRefresh) {
        inFlightRefresh = refreshTokens(getApi, tokens).finally(() => {
          inFlightRefresh = null;
        });
      }

      return inFlightRefresh;
    }

    return tokens.accessToken;
  };
};
